/**
 * App-wide library of attached folders.
 *
 * The user can attach multiple folders across chats and pick from them in
 * the prompt-input selector. Light metadata (id + name + last-used time)
 * lives in localStorage so the selector can render without touching
 * IndexedDB on every render. The actual `FileSystemDirectoryHandle` lives
 * in IndexedDB keyed by the same id.
 *
 * The per-chat "which folder is active" mapping also lives in localStorage
 * so reloading a chat restores the right folder without a picker.
 *
 * Legacy (pre-library) chats stored handles keyed by chatId in the same
 * IndexedDB database. `migrateLegacyChatHandle()` pulls those records into
 * the library on first read.
 *
 * @module file-agent/lib/folder-library
 */

const LIBRARY_KEY = "taqat:file-agent:folder-library";
const ACTIVE_FOLDER_PREFIX = "taqat:file-agent:active-folder:";

const DB_NAME = "taqat-file-agent";
// Bump from 1 to 2 to add the new folder-handles store; legacy stores are
// preserved so the migration helper can read them.
const DB_VERSION = 2;
const FOLDER_HANDLE_STORE = "folder-handles";
const LEGACY_HANDLE_STORE = "handles";

/** Object store name for pre-edit file snapshots — re-exported for the snapshot helpers. */
export const SNAPSHOT_STORE = "snapshots";

/** Light metadata persisted in localStorage. */
export interface FolderLibraryEntry {
  /** Stable id used to key the IndexedDB handle record. */
  id: string;
  /** Display name (the folder's basename when first picked). */
  name: string;
  /** Epoch ms — used to sort the dropdown by recency. */
  lastUsedAt: number;
}

interface FolderHandleRecord {
  id: string;
  handle: FileSystemDirectoryHandle;
  folderName: string;
  attachedAt: number;
}

interface LegacyHandleRecord {
  chatId: string;
  handle: FileSystemDirectoryHandle;
  folderName: string;
  attachedAt: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Single source of truth for the file-agent's IndexedDB connection.
 * Both the folder library and the snapshot helpers go through this so we
 * never end up with competing `version` upgrades opening the same DB.
 */
export function openFileAgentDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        // The legacy handles + snapshots stores already exist from v1; we
        // create the new folder-handles store alongside without touching
        // them so existing chats keep working through the migration step.
        if (!db.objectStoreNames.contains(FOLDER_HANDLE_STORE)) {
          db.createObjectStore(FOLDER_HANDLE_STORE, { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains(LEGACY_HANDLE_STORE)) {
          db.createObjectStore(LEGACY_HANDLE_STORE, { keyPath: "chatId" });
        }
        if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
          const store = db.createObjectStore(SNAPSHOT_STORE, { keyPath: "key" });
          store.createIndex("by-chat", "chatId", { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
}

export function awaitReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function newId(): string {
  // crypto.randomUUID is available in every browser that supports the
  // File System Access API — this hook only runs there.
  return crypto.randomUUID();
}

/** Read the library list, sorted by most-recently-used first. */
export function getLibrary(): FolderLibraryEntry[] {
  try {
    const raw = localStorage.getItem(LIBRARY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as FolderLibraryEntry[];
    return [...parsed].sort((a, b) => b.lastUsedAt - a.lastUsedAt);
  } catch {
    return [];
  }
}

function writeLibrary(entries: FolderLibraryEntry[]): void {
  localStorage.setItem(LIBRARY_KEY, JSON.stringify(entries));
}

/**
 * Insert or update a library entry. Returns the id (always equal to the
 * passed id when present, otherwise a freshly generated one).
 */
export function upsertLibraryEntry(
  partial: { id?: string; name: string },
): FolderLibraryEntry {
  const list = getLibrary();
  const id = partial.id ?? newId();
  const now = Date.now();
  const existingIndex = list.findIndex((e) => e.id === id);
  const entry: FolderLibraryEntry = {
    id,
    name: partial.name,
    lastUsedAt: now,
  };
  if (existingIndex >= 0) {
    list[existingIndex] = { ...list[existingIndex], ...entry };
  } else {
    list.push(entry);
  }
  writeLibrary(list);
  return entry;
}

/** Remove an entry from the library and delete its IndexedDB handle. */
export async function removeLibraryEntry(id: string): Promise<void> {
  const list = getLibrary().filter((e) => e.id !== id);
  writeLibrary(list);
  const db = await openFileAgentDb();
  await awaitReq(
    db.transaction(FOLDER_HANDLE_STORE, "readwrite")
      .objectStore(FOLDER_HANDLE_STORE)
      .delete(id),
  );
}

/** Bump `lastUsedAt` so the entry floats to the top of the dropdown. */
export function touchLibraryEntry(id: string): void {
  const list = getLibrary();
  const idx = list.findIndex((e) => e.id === id);
  if (idx < 0) return;
  list[idx] = { ...list[idx], lastUsedAt: Date.now() } as FolderLibraryEntry;
  writeLibrary(list);
}

/** Save (or replace) the directory handle for a library id. */
export async function saveFolderHandle(
  id: string,
  handle: FileSystemDirectoryHandle,
): Promise<void> {
  const db = await openFileAgentDb();
  const record: FolderHandleRecord = {
    id,
    handle,
    folderName: handle.name,
    attachedAt: Date.now(),
  };
  await awaitReq(
    db.transaction(FOLDER_HANDLE_STORE, "readwrite")
      .objectStore(FOLDER_HANDLE_STORE)
      .put(record),
  );
}

/** Load a previously-saved handle. Returns null when missing. */
export async function loadFolderHandle(
  id: string,
): Promise<FileSystemDirectoryHandle | null> {
  const db = await openFileAgentDb();
  const record = await awaitReq<FolderHandleRecord | undefined>(
    db.transaction(FOLDER_HANDLE_STORE, "readonly")
      .objectStore(FOLDER_HANDLE_STORE)
      .get(id),
  );
  return record?.handle ?? null;
}

/** Read which library folder is active for a given chat (if any). */
export function getActiveFolderId(chatId: string): string | null {
  return localStorage.getItem(ACTIVE_FOLDER_PREFIX + chatId);
}

/** Pin a library folder as the active one for a chat. */
export function setActiveFolderId(chatId: string, id: string): void {
  localStorage.setItem(ACTIVE_FOLDER_PREFIX + chatId, id);
}

/** Forget the active folder mapping for a chat. */
export function clearActiveFolderId(chatId: string): void {
  localStorage.removeItem(ACTIVE_FOLDER_PREFIX + chatId);
}

/**
 * Pull a legacy chatId-keyed handle into the library schema if one exists.
 *
 * Returns the new library id when migration ran, or null when the chat had
 * no legacy record. Idempotent: subsequent calls with the same chatId
 * return null because the legacy record is removed after migration.
 */
export async function migrateLegacyChatHandle(
  chatId: string,
): Promise<string | null> {
  const db = await openFileAgentDb();
  if (!db.objectStoreNames.contains(LEGACY_HANDLE_STORE)) return null;

  const legacy = await awaitReq<LegacyHandleRecord | undefined>(
    db.transaction(LEGACY_HANDLE_STORE, "readonly")
      .objectStore(LEGACY_HANDLE_STORE)
      .get(chatId),
  );
  if (!legacy) return null;

  const entry = upsertLibraryEntry({ name: legacy.folderName });
  await saveFolderHandle(entry.id, legacy.handle);
  setActiveFolderId(chatId, entry.id);

  await awaitReq(
    db.transaction(LEGACY_HANDLE_STORE, "readwrite")
      .objectStore(LEGACY_HANDLE_STORE)
      .delete(chatId),
  );

  return entry.id;
}
